import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ChevronLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-emerald-500" />
            <h1 className="text-xl font-bold text-slate-900">Privacy Policy</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
          <p className="text-sm text-slate-500 mb-6">Last updated: February 16, 2026</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              StelliSocial ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our social media management platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Information We Collect</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Account information (name, email address, password)</li>
              <li>Profile information from connected social media accounts</li>
              <li>Content you create, upload, or share through our platform</li>
              <li>Communications with us (support requests, feedback)</li>
              <li>Payment information (processed securely through Stripe)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Social Media Platform Access</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              When you connect your social media accounts (Facebook, Instagram, LinkedIn, Twitter/X, TikTok, YouTube, Pinterest, Snapchat, Threads, Bluesky, Google Business Profile), we request access to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Read your profile information</li>
              <li>Post content on your behalf (when you schedule posts)</li>
              <li>Read engagement metrics and analytics</li>
              <li>Manage pages/accounts you've granted us access to</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              We only access the permissions you explicitly grant and use this access solely to provide our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. How We Use Your Information</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Provide, maintain, and improve our services</li>
              <li>Schedule and publish content to your connected social accounts</li>
              <li>Generate analytics and insights about your social media performance</li>
              <li>Send you notifications about your content and account</li>
              <li>Process payments and manage your subscription</li>
              <li>Respond to your requests and provide customer support</li>
              <li>Protect against fraud and unauthorized access</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Social media platforms (to post content you've scheduled)</li>
              <li>Service providers (payment processing, email delivery, hosting)</li>
              <li>Legal authorities (when required by law)</li>
              <li>Team members you've invited to collaborate</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement industry-standard security measures to protect your data, including encryption in transit and at rest, secure authentication, and regular security audits. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your information for as long as your account is active or as needed to provide services. You can request deletion of your data at any time by contacting us or deleting your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Your Rights</h2>
            <p className="text-slate-600 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your account and data</li>
              <li>Disconnect social media accounts at any time</li>
              <li>Export your data</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Cookies and Tracking</h2>
            <p className="text-slate-600 leading-relaxed">
              We use essential cookies to maintain your session and preferences. We do not use third-party tracking cookies for advertising purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-slate-600 mt-2">
              <strong>Email:</strong> privacy@stellisocial.com
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
